import { FiImage, FiX } from 'react-icons/fi';
import './survey.css';

/**
 * Renderiza una pregunta de encuesta según su `type`. No sabe nada de
 * pasos/bloques ni de `dependsOn` — eso lo resuelve la vista que la
 * usa. Solo recibe el valor actual y avisa los cambios hacia arriba
 * vía `onChange(value, validationError?)`.
 *
 * @param {{
 *   question: import('../../services/surveyService').SurveyQuestion,
 *   value: any,
 *   error?: string,
 *   onChange: (value: any, validationError?: string|null) => void,
 * }} props
 */
export default function SurveyQuestionField({ question, value, error, onChange }) {
  const fieldId = `survey-q-${question.id}`;

  return (
    <div className={`survey-field${error ? ' has-error' : ''}`}>
      <label className="survey-field__label" htmlFor={fieldId}>
        {question.text}
        {question.required && <span className="survey-field__required">*</span>}
      </label>

      {question.type === 'single_choice' && (
        <div className="survey-options" id={fieldId} role="radiogroup" aria-label={question.text}>
          {question.options.map((option) => (
            <button
              type="button"
              key={option.id}
              className={`survey-option${value === option.value ? ' is-selected' : ''}`}
              role="radio"
              aria-checked={value === option.value}
              onClick={() => onChange(option.value, null)}
            >
              <span className="survey-option__radio" aria-hidden="true" />
              <span>{option.text}</span>
            </button>
          ))}
        </div>
      )}

      {question.type === 'short_text' && (
        <input
          id={fieldId}
          className="survey-input"
          type={question.inputType || 'text'}
          inputMode={question.inputType === 'tel' ? 'tel' : undefined}
          maxLength={question.maxLength || undefined}
          placeholder={question.placeholder}
          value={value || ''}
          onChange={(e) => onChange(e.target.value, null)}
        />
      )}

      {question.type === 'long_text' && (
        <textarea
          id={fieldId}
          className="survey-textarea"
          rows={4}
          placeholder={question.placeholder}
          value={value || ''}
          onChange={(e) => onChange(e.target.value, null)}
        />
      )}

      {question.type === 'image' && (
        <ImagePicker fieldId={fieldId} question={question} value={value} onChange={onChange} />
      )}

      {error && <span className="survey-field__error">{error}</span>}
    </div>
  );
}

function ImagePicker({ fieldId, question, value, onChange }) {
  function handleFile(event) {
    const file = event.target.files?.[0];
    event.target.value = ''; // permite volver a elegir el mismo archivo si lo quita y lo reintenta
    if (!file) return;

    if (question.maxSizeMb && file.size > question.maxSizeMb * 1024 * 1024) {
      onChange(null, `La imagen supera el máximo de ${question.maxSizeMb}MB.`);
      return;
    }
    onChange({ file, name: file.name, previewUrl: URL.createObjectURL(file) }, null);
  }

  if (value?.previewUrl) {
    return (
      <div className="survey-image">
        <img src={value.previewUrl} alt={question.text} />
        <button
          type="button"
          className="survey-image__remove"
          onClick={() => onChange(null, null)}
          aria-label="Quitar imagen"
        >
          <FiX />
        </button>
      </div>
    );
  }

  return (
    <label className="survey-image-picker" htmlFor={fieldId}>
      <FiImage />
      <span>Tomar o elegir foto</span>
      <input
        id={fieldId}
        type="file"
        accept={question.accept || 'image/*'}
        capture="environment"
        onChange={handleFile}
      />
    </label>
  );
}
